import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  Spinner,
  DataTable,
  Banner,
  Button,
} from '@shopify/polaris';
import axios from 'axios';
import ErrorMessage from '../components/ErrorMessage';
import { useAuth } from '../contexts/AuthContext';

interface DashboardStats {
  totalContacts: number;
  totalCallLogs: number;
  recentCallLogs: Array<{
    id: number;
    duration: number;
    status: string;
    direction: string;
    created_at: string;
  }>;
}

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (user?.role !== 'support_manager') {
      fetchDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      setLoading(true);
      
      const callLogsRes = await axios.get('/call_logs?limit=5');

      // Add null checks and default values
      const callLogs = callLogsRes.data?.data?.call_logs || [];

      setStats({
        totalContacts: 0,
        totalCallLogs: callLogs.length,
        recentCallLogs: callLogs,
      });
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      setError(error);
      // Set default values on error
      setStats({
        totalContacts: 0,
        totalCallLogs: 0,
        recentCallLogs: [],
      });
    } finally {
      setLoading(false);
    }
  };

  // Support Manager Dashboard
  if (user?.role === 'support_manager') {
    return (
      <Page title="Support Dashboard" fullWidth>
        <Layout>
          <Layout.Section>
            <Banner tone="info">
              <p>
                Welcome to the Support Dashboard. As a support manager, you have access to client management features.
                You can view and manage client accounts, billing information, and usage statistics.
              </p>
            </Banner>
          </Layout.Section>
          
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h2">
                  Support Manager Access
                </Text>
                <Text variant="bodyMd" as="p">
                  You are logged in as a support manager. Your role allows you to:
                </Text>
                <BlockStack gap="200">
                  <Text variant="bodyMd" as="p">• View and manage client accounts</Text>
                  <Text variant="bodyMd" as="p">• Monitor client usage and billing</Text>
                  <Text variant="bodyMd" as="p">• Add credits to client accounts</Text>
                  <Text variant="bodyMd" as="p">• Assign pricing plans to clients</Text>
                  <Text variant="bodyMd" as="p">• Access system settings and configuration</Text>
                </BlockStack>
                <Text variant="bodyMd" as="p" tone="subdued">
                  For detailed client management, please use the dedicated support management interface.
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <ErrorMessage
        title="Unable to load dashboard"
        message="We encountered an issue while loading your dashboard data. This might be due to a network connection problem or a temporary service issue."
        error={error}
        onRetry={fetchDashboardData}
        variant="inline"
      />
    );
  }

  if (loading) {
    return (
      <Page title="Dashboard" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p">Loading dashboard...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const recentCallsRows = stats && Array.isArray(stats.recentCallLogs) 
    ? stats.recentCallLogs.map((call) => [
        'N/A', // Phone/ID not available in this data structure
        `${Math.floor(call.duration / 60)}:${(call.duration % 60).toString().padStart(2, '0')}`,
        call.status,
        call.direction,
        new Date(call.created_at).toLocaleDateString(),
      ])
    : [];

  return (
    <Page title="Dashboard" fullWidth>
      <Layout>
        <Layout.Section>
          <BlockStack gap="500">
            <Text variant="headingLg" as="h1">
              Welcome to Selldo AI Transcript
            </Text>

            <Layout>
              <Layout.Section>
                <Card>
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text variant="headingMd" as="h2">
                        {stats?.totalCallLogs || 0}
                      </Text>
                      <Badge tone="success">Total</Badge>
                    </InlineStack>
                    <Text variant="bodyMd" as="p" tone="subdued">
                      Call Logs
                    </Text>
                  </BlockStack>
                </Card>
              </Layout.Section>

              <Layout.Section>
                <Card>
                  <BlockStack gap="200">
                    <InlineStack align="space-between">
                      <Text variant="headingMd" as="h2">
                        Coming Soon
                      </Text>
                      <Badge tone="warning">New</Badge>
                    </InlineStack>
                    <Text variant="bodyMd" as="p" tone="subdued">
                      AI Analytics
                    </Text>
                  </BlockStack>
                </Card>
              </Layout.Section>
            </Layout>

            <Card>
              <BlockStack gap="400">
                <Text variant="headingMd" as="h3">
                  Recent Call Logs
                </Text>
                {recentCallsRows.length > 0 ? (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                    headings={['Phone/ID', 'Duration', 'Status', 'Direction', 'Date']}
                    rows={recentCallsRows}
                  />
                ) : (
                  <div style={{ padding: '1rem', textAlign: 'center' }}>
                    <Text variant="bodyMd" as="p" tone="subdued">
                      No recent call logs found
                    </Text>
                  </div>
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Dashboard;
